import { FC } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTriangleExclamation } from "@fortawesome/pro-solid-svg-icons";
import { jwtDecode } from "jwt-decode";
import {
  isImpersonating,
  getImpersonatedEmail,
  endImpersonation,
  IMPERSONATION_BANNER_HEIGHT_PX,
} from "../utils/impersonation";

function getTenantIdFromAccessToken(): string | null {
  try {
    const accessToken = localStorage.getItem("access_token");
    if (!accessToken) return null;

    const decoded = jwtDecode<{ mkClaims?: { tenantId?: string } }>(
      accessToken
    );
    return decoded.mkClaims?.tenantId || null;
  } catch {
    return null;
  }
}

export const ImpersonationBanner: FC = () => {
  const theme = useTheme();

  if (!isImpersonating()) {
    return null;
  }

  const impersonatedEmail = getImpersonatedEmail();
  const tenantId = getTenantIdFromAccessToken();

  const handleEndImpersonation = () => {
    endImpersonation();
    window.location.reload();
  };

  return (
    <Box
      id="impersonation-banner"
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: theme.zIndex.appBar + 1,
        height: `${IMPERSONATION_BANNER_HEIGHT_PX}px`,
        bgcolor: "#00af89",
        color: "#ffffff",
        boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
      }}
      role="status"
      aria-live="polite"
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        sx={{
          position: "relative",
          height: "100%",
          minWidth: 0,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ minWidth: 0, overflow: "hidden" }}
        >
          <FontAwesomeIcon
            id="impersonation-banner-warning-icon"
            icon={faTriangleExclamation}
            color="orange"
            style={{ flexShrink: 0, opacity: 0.95 }}
          />
          <Typography
            id="impersonation-banner-text"
            variant="caption"
            sx={{ color: "inherit", whiteSpace: "nowrap", minWidth: 0 }}
            noWrap
          >
            You are impersonating{" "}
            <Box component="b" id="impersonation-banner-email">
              {impersonatedEmail || "a user"}
            </Box>
            {tenantId ? (
              <>
                {" "}
                on organization{" "}
                <Box component="b" id="impersonation-banner-tenant-id">
                  {tenantId}
                </Box>
              </>
            ) : null}
          </Typography>
        </Stack>

        <Button
          id="impersonation-banner-end-button"
          size="small"
          onClick={handleEndImpersonation}
          variant="text"
          sx={{
            color: "inherit",
            position: "absolute",
            right: 16,
            "&:hover": { textDecoration: "underline" },
          }}
        >
          End impersonation
        </Button>
      </Stack>
    </Box>
  );
};
